import Card from 'react-bootstrap/Card'
import Skeleton from 'react-loading-skeleton'
import styles from '../styles/NavigationPanel.module.scss'
import { useNavigate } from 'react-router-dom'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { v4 } from 'uuid'

const NavigationPanel = () => {
  const { dir, subpage } = useParams()
  const data = useSelector((state) => state.pagedata.data)
  const isLoading = useSelector((state) => state.pagedata.isLoading)
  const navigate = useNavigate()

  const card = data?.container?.json_dict?.card
  const partnercounts = data?.panels?.partnercounts
  const tribelinks = data?.panels?.tribelinks

  const changeTheme = (theme) =>
    navigate(`/${dir}/${card.sanitized_wo}${theme}`, { noTop: true })
  const isLoadingLongNav =
    isLoading && ['average-decks', 'commanders', 'decks'].includes(dir)
  const defaultBudgets = [
    { 'href-suffix': '#', html: <Skeleton /> },
    { 'href-suffix': '#', html: <Skeleton /> },
  ]

  return (
    <>
      <h5 className='title'>Navigation</h5>
      <Card className='shadow-sm'>
        <Card.Body>
          {(isLoadingLongNav ||
            (tribelinks && tribelinks.themes.length > 1)) && (
            <div className={styles.theme}>
              <h5>{isLoadingLongNav ? <Skeleton /> : 'By Theme'}</h5>
              {isLoadingLongNav ? (
                <h1>
                  <Skeleton />
                </h1>
              ) : (
                <select
                  className='form-control'
                  onChange={(e) => changeTheme(e.target.value)}
                  value={`/${subpage}`}
                >
                  <option></option>
                  {tribelinks?.themes.map((tribelink) => (
                    <option
                      value={tribelink['href-suffix']}
                      key={tribelink['href-suffix']}
                    >
                      {tribelink.value} ({tribelink.count})
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
          {(isLoadingLongNav ||
            (tribelinks && tribelinks.budget.length > 1)) && (
            <div className={styles.budget}>
              <h5>{isLoadingLongNav ? <Skeleton /> : 'By Budget'}</h5>
              <div className='form-check'>
                <input
                  checked={!['budget', 'expensive'].includes(subpage)}
                  className='form-check-input'
                  id='all'
                  onChange={() => changeTheme('')}
                  type='radio'
                />
                {isLoadingLongNav ? (
                  <Skeleton />
                ) : (
                  <label className='form-check-label' htmlFor='all'>
                    All
                  </label>
                )}
              </div>
              {(isLoadingLongNav ? defaultBudgets : tribelinks?.budget).map(
                (item) => (
                  <div className='form-check' key={v4()}>
                    <input
                      checked={subpage === item['href-suffix'].slice(1)}
                      className='form-check-input'
                      id={item['href-suffix']}
                      onChange={(e) => changeTheme(e.target.id)}
                      type='radio'
                    />
                    {item?.html || (
                      <label
                        className='form-check-label'
                        htmlFor={item['href-suffix']}
                      >
                        {item.value}
                      </label>
                    )}
                  </div>
                )
              )}
            </div>
          )}
          {partnercounts && (
            <div className={styles.theme}>
              <h5>{card.legal_partner ? 'Partners' : 'Backgrounds'}</h5>
              <select
                className='form-control'
                onChange={(e) => navigate(e.target.value, { noTop: true })}
              >
                <option></option>
                {partnercounts.map((item) => (
                  <option value={item.href} key={item.href}>
                    {item.value}
                  </option>
                ))}
              </select>
            </div>
          )}
          {((tribelinks && (tribelinks.themes || tribelinks.budget)) ||
            isLoadingLongNav) && <h5>Links</h5>}
          <div className={styles.relatedLinks}>
            {isLoading && !card ? (
              <>
                <Skeleton />
                <Skeleton />
                <Skeleton />
                <Skeleton />
                <Skeleton />
                <Skeleton />
                <Skeleton />
                <Skeleton />
              </>
            ) : (
              [
                {
                  href: (card) =>
                    card.aetherhub_uri &&
                    !card.aetherhub_uri.startsWith('https://')
                      ? `https://aetherhub.com/Meta/Format/Commander/?com=${card.aetherhub_uri}&updated=all-time`
                      : card.aetherhub_uri,
                  value: 'Aetherhub',
                },
                {
                  href: (card) =>
                    card.archidekt_uri &&
                    !card.archidekt_uri.startsWith('https://')
                      ? `https://archidekt.com/search/decks?commanders=${card.archidekt_uri}&formats=3`
                      : card.archidekt_uri,
                  value: 'Archidekt',
                },
                {
                  href: (card) => {
                    const query = card.legal_commander
                      ? 'search_cards_commander'
                      : 'search_cards'
                    return card.deckstats_uri &&
                      !card.deckstats_uri.startsWith('https://')
                      ? `https://deckstats.net/decks/search/?${query}[]=${card.deckstats_uri}&search_format=10`
                      : card.deckstats_uri.replace('search_cards', query)
                  },
                  value: 'Deckstats',
                },
                {
                  href: (card) =>
                    card.gatherer_uri &&
                    !card.gatherer_uri.startsWith('https://')
                      ? `https://gatherer.wizards.com/Pages/Card/Details.aspx?multiverseid=${card.gatherer_uri}`
                      : card.gatherer_uri,
                  value: 'Gatherer',
                },
                {
                  href: (card) =>
                    card.moxfield_uri &&
                    !card.moxfield_uri.startsWith('https://')
                      ? `https://www.moxfield.com/decks/public?format=commander&filter=${card.moxfield_uri}`
                      : card.moxfield_uri,
                  value: 'Moxfield',
                },
                {
                  href: (card) =>
                    card.mtggoldfish_uri &&
                    !card.mtggoldfish_uri?.startsWith('https://')
                      ? `https://www.mtggoldfish.com/deck/custom?mformat=commander&commander=${card.mtggoldfish_uri}`
                      : card.mtggoldfish_uri,
                  value: 'MTGGoldfish',
                },
                {
                  href: (card) => card.prices.mtgstocks?.url,
                  value: 'MTGStocks',
                },
                {
                  href: (card) =>
                    card.scryfall_uri &&
                    !card.scryfall_uri?.startsWith('https://')
                      ? `https://scryfall.com/card/${card.scryfall_uri}?utm_source=api`
                      : card.scryfall_uri,
                  value: 'Scryfall',
                },
              ]
                .filter(({ href }) => href(card))
                .map(({ href, value }) => (
                  <div key={value}>
                    <a href={href(card)} rel='noreferrer' target='_blank'>
                      {value}
                    </a>
                  </div>
                ))
            )}
          </div>
        </Card.Body>
      </Card>
    </>
  )
}

export default NavigationPanel
